export class GallerySlider {
  constructor() {
    this.root = document.querySelector('.l-gallery');
    if (!this.root) {
      return;
    }

    this.list = this.root.querySelector('.l-gallery__list');
    this.items = Array.from(this.root.querySelectorAll('.l-gallery__item'));
    this.indicatorThumb = this.root.querySelector('.l-gallery__indicator-thumb');
    this.prevButton = this.root.querySelector('.l-gallery__arrow--prev');
    this.nextButton = this.root.querySelector('.l-gallery__arrow--next');
    this.resizeRaf = null;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.dragPointerId = null;
    this.isPointerDown = false;
    this.isDragging = false;
    this.suppressClick = false;
    this.wheelTimer = null;
    this.isInitialized = false;

    if (
      !this.list ||
      !this.indicatorThumb ||
      !this.prevButton ||
      !this.nextButton ||
      this.items.length === 0
    ) {
      return;
    }

    this.onResize = this.handleResize.bind(this);
    this.onScroll = this.updateIndicator.bind(this);
    this.onPrevClick = () => this.scrollByPage(-1);
    this.onNextClick = () => this.scrollByPage(1);
    this.onPointerDown = (event) => this.handleDragStart(event);
    this.onPointerMove = (event) => this.handleDragMove(event);
    this.onPointerUp = (event) => this.handleDragEnd(event);
    this.onClick = (event) => this.handleClick(event);
    this.onDragStart = (event) => event.preventDefault();
    this.onWheel = (event) => this.handleWheel(event);

    this.bindEvents();
    this.updateIndicator();
    this.isInitialized = true;
  }

  bindEvents() {
    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.prevButton.addEventListener('click', this.onPrevClick);
    this.nextButton.addEventListener('click', this.onNextClick);
    this.list.addEventListener('pointerdown', this.onPointerDown);
    this.list.addEventListener('pointermove', this.onPointerMove);
    this.list.addEventListener('pointerup', this.onPointerUp);
    this.list.addEventListener('pointercancel', this.onPointerUp);
    this.list.addEventListener('lostpointercapture', this.onPointerUp);
    this.list.addEventListener('click', this.onClick, true);
    this.list.addEventListener('dragstart', this.onDragStart);
    this.list.addEventListener('wheel', this.onWheel, { passive: false });

    this.list.querySelectorAll('a, img').forEach((element) => {
      element.draggable = false;
    });
  }

  destroy() {
    if (!this.isInitialized) {
      return;
    }

    this.list.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.prevButton.removeEventListener('click', this.onPrevClick);
    this.nextButton.removeEventListener('click', this.onNextClick);
    this.list.removeEventListener('pointerdown', this.onPointerDown);
    this.list.removeEventListener('pointermove', this.onPointerMove);
    this.list.removeEventListener('pointerup', this.onPointerUp);
    this.list.removeEventListener('pointercancel', this.onPointerUp);
    this.list.removeEventListener('lostpointercapture', this.onPointerUp);
    this.list.removeEventListener('click', this.onClick, true);
    this.list.removeEventListener('dragstart', this.onDragStart);
    this.list.removeEventListener('wheel', this.onWheel);

    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }

    if (this.wheelTimer) {
      clearTimeout(this.wheelTimer);
      this.wheelTimer = null;
    }
  }

  handleResize() {
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }

    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      this.updateIndicator();
    });
  }

  scrollByPage(direction) {
    const currentIndex = this.getNearestItemIndex();
    const targetIndex = Math.max(
      0,
      Math.min(this.items.length - 1, currentIndex + direction),
    );

    this.scrollToItemIndex(targetIndex);
  }

  getScrollStep() {
    const firstItem = this.items[0];
    const listStyle = window.getComputedStyle(this.list);
    const gap = parseFloat(listStyle.columnGap) || parseFloat(listStyle.gap) || 0;

    return firstItem
      ? firstItem.getBoundingClientRect().width + gap
      : this.list.clientWidth;
  }

  getNearestItemIndex() {
    const step = this.getScrollStep();

    if (step <= 0) {
      return 0;
    }

    return Math.max(
      0,
      Math.min(this.items.length - 1, Math.round(this.list.scrollLeft / step)),
    );
  }

  scrollToItemIndex(index) {
    const step = this.getScrollStep();
    const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
    const targetLeft = Math.min(maxScroll, Math.max(0, index * step));

    this.list.scrollTo({
      left: targetLeft,
      behavior: 'smooth',
    });
  }

  handleWheel(event) {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
      return;
    }

    event.preventDefault();

    if (Math.abs(event.deltaX) < 8) {
      return;
    }

    if (this.wheelTimer) {
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => {
        this.wheelTimer = null;
      }, 120);
      return;
    }

    this.scrollByPage(event.deltaX > 0 ? 1 : -1);
    this.wheelTimer = setTimeout(() => {
      this.wheelTimer = null;
    }, 120);
  }

  handleDragStart(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }

    this.isPointerDown = true;
    this.isDragging = false;
    this.suppressClick = false;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.list.scrollLeft;
    this.dragPointerId = event.pointerId;
  }

  handleDragMove(event) {
    if (!this.isPointerDown || event.pointerType !== 'mouse') {
      return;
    }

    const dragDistance = event.clientX - this.dragStartX;

    if (!this.isDragging && Math.abs(dragDistance) < 5) {
      return;
    }

    this.isDragging = true;
    this.suppressClick = true;
    this.list.classList.add('is-dragging');

    if (!this.list.hasPointerCapture(event.pointerId)) {
      this.list.setPointerCapture(event.pointerId);
    }

    this.list.scrollLeft = this.dragStartScrollLeft - dragDistance;
    event.preventDefault();
  }

  handleDragEnd() {
    if (!this.isPointerDown) {
      return;
    }

    const wasDragging = this.isDragging;

    this.isPointerDown = false;
    this.isDragging = false;
    this.list.classList.remove('is-dragging');

    if (
      this.dragPointerId !== null &&
      this.list.hasPointerCapture(this.dragPointerId)
    ) {
      this.list.releasePointerCapture(this.dragPointerId);
    }

    this.dragPointerId = null;

    if (wasDragging) {
      this.snapToNearestItem();
    }
  }

  handleClick(event) {
    if (!this.suppressClick) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.suppressClick = false;
  }

  snapToNearestItem() {
    if (this.items.length === 0) {
      return;
    }

    this.scrollToItemIndex(this.getNearestItemIndex());
  }

  updateIndicator() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const progress = maxScroll > 0 ? this.list.scrollLeft / maxScroll : 0;
    const thumbWidth = maxScroll > 0 ? Math.max(progress * 100, 5) : 100;

    this.indicatorThumb.style.width = `${thumbWidth}%`;
    this.indicatorThumb.style.left = '0';

    this.prevButton.disabled = this.list.scrollLeft <= 1;
    this.nextButton.disabled = this.list.scrollLeft >= maxScroll - 1;
  }
}

export class GalleryLargeSlider {
  constructor() {
    this.roots = Array.from(document.querySelectorAll('.l-gallery-large'));
    this.instances = [];
    this.onResize = null;
    this.isInitialized = false;

    if (this.roots.length === 0) {
      return;
    }

    this.instances = this.roots
      .map((root) => this.createInstance(root))
      .filter((instance) => instance !== null);

    this.onResize = () => this.instances.forEach((inst) => this.handleResize(inst));
    window.addEventListener('resize', this.onResize);
    this.isInitialized = true;
  }

  createInstance(root) {
    const list = root.querySelector('.l-gallery-large__list');
    const items = Array.from(root.querySelectorAll('.l-gallery-large__item'));

    if (!list || items.length === 0) {
      return null;
    }

    const instance = {
      root,
      list,
      items,
      currentIndex: 0,
      targetIndex: 0,
      scrollRaf: null,
      scrollEndTimer: null,
      scrollEndChecks: 0,
      isProgrammaticScroll: false,
      resizeRaf: null,
      nav: items.length > 1 ? this.createNavigation(root, items) : null,
    };

    instance.onScroll = () => this.handleScroll(instance);
    instance.onScrollEnd = () => this.finishProgrammaticScroll(instance);
    instance.onUserScrollStart = () => this.cancelProgrammaticScroll(instance);
    instance.onPrevClick = () => this.scrollToIndex(instance, instance.currentIndex - 1);
    instance.onNextClick = () => this.scrollToIndex(instance, instance.currentIndex + 1);
    instance.onThumbClick = (event) => this.handleThumbClick(instance, event);

    this.bindEvents(instance);
    this.sync(instance, false);

    return instance;
  }

  createNavigation(root, items) {
    const thumbsWrap = document.createElement('div');
    thumbsWrap.className = 'l-gallery-large__thumbs-wrap';

    const thumbs = document.createElement('ul');
    thumbs.className = 'l-gallery-large__thumbs';

    items.forEach((item, index) => {
      const image = item.querySelector('.l-gallery-large__img');
      const thumbItem = document.createElement('li');
      thumbItem.className = 'l-gallery-large__thumb-item';

      const thumbButton = document.createElement('button');
      thumbButton.className = 'l-gallery-large__thumb';
      thumbButton.type = 'button';
      thumbButton.dataset.index = String(index);
      thumbButton.setAttribute('aria-label', `${index + 1}枚目を表示`);

      if (image) {
        const thumbImage = document.createElement('img');
        thumbImage.className = 'l-gallery-large__thumb-image';
        thumbImage.src = this.getActiveSrc(image);
        thumbImage.alt = image.getAttribute('alt') || '';
        thumbButton.appendChild(thumbImage);
      }

      thumbItem.appendChild(thumbButton);
      thumbs.appendChild(thumbItem);
    });

    const prevButton = document.createElement('button');
    prevButton.className = 'l-gallery-large__arrow l-gallery-large__arrow--prev';
    prevButton.type = 'button';
    prevButton.setAttribute('aria-label', '前へ');

    const nextButton = document.createElement('button');
    nextButton.className = 'l-gallery-large__arrow l-gallery-large__arrow--next';
    nextButton.type = 'button';
    nextButton.setAttribute('aria-label', '次へ');

    const arrowNav = document.createElement('div');
    arrowNav.className = 'l-gallery-large__nav';
    arrowNav.append(prevButton, nextButton);

    thumbsWrap.appendChild(thumbs);
    root.appendChild(arrowNav);
    root.appendChild(thumbsWrap);

    return {
      arrowNav,
      thumbsWrap,
      thumbs,
      thumbButtons: Array.from(thumbs.querySelectorAll('.l-gallery-large__thumb')),
      prevButton,
      nextButton,
    };
  }

  bindEvents(instance) {
    instance.list.addEventListener('scroll', instance.onScroll, { passive: true });
    instance.list.addEventListener('scrollend', instance.onScrollEnd);
    instance.list.addEventListener('pointerdown', instance.onUserScrollStart, { passive: true });
    instance.list.addEventListener('touchstart', instance.onUserScrollStart, { passive: true });
    instance.list.addEventListener('wheel', instance.onUserScrollStart, { passive: true });

    if (instance.nav) {
      instance.nav.prevButton.addEventListener('click', instance.onPrevClick);
      instance.nav.nextButton.addEventListener('click', instance.onNextClick);
      instance.nav.thumbs.addEventListener('click', instance.onThumbClick);
    }
  }

  destroy() {
    if (!this.isInitialized) {
      return;
    }

    window.removeEventListener('resize', this.onResize);

    this.instances.forEach((instance) => {
      instance.list.removeEventListener('scroll', instance.onScroll);
      instance.list.removeEventListener('scrollend', instance.onScrollEnd);
      instance.list.removeEventListener('pointerdown', instance.onUserScrollStart);
      instance.list.removeEventListener('touchstart', instance.onUserScrollStart);
      instance.list.removeEventListener('wheel', instance.onUserScrollStart);

      if (instance.nav) {
        instance.nav.prevButton.removeEventListener('click', instance.onPrevClick);
        instance.nav.nextButton.removeEventListener('click', instance.onNextClick);
        instance.nav.thumbs.removeEventListener('click', instance.onThumbClick);
      }

      if (instance.scrollRaf) {
        cancelAnimationFrame(instance.scrollRaf);
        instance.scrollRaf = null;
      }

      if (instance.scrollEndTimer) {
        clearTimeout(instance.scrollEndTimer);
        instance.scrollEndTimer = null;
      }

      if (instance.resizeRaf) {
        cancelAnimationFrame(instance.resizeRaf);
        instance.resizeRaf = null;
      }
    });
  }

  getActiveSrc(imgEl) {
    const getFirstSrc = (value) => {
      if (!value) {
        return '';
      }

      return value
        .split(',')
        .map((entry) => entry.trim().split(/\s+/)[0])
        .find(Boolean) || '';
    };

    const picture = imgEl.closest('picture');
    if (picture) {
      const sources = Array.from(picture.querySelectorAll('source'));
      for (const source of sources) {
        const media = source.getAttribute('media');
        if (!media || window.matchMedia(media).matches) {
          return getFirstSrc(source.getAttribute('srcset') || source.getAttribute('src'));
        }
      }
    }
    return getFirstSrc(imgEl.currentSrc || imgEl.getAttribute('src'));
  }

  getItemLeft(instance, index) {
    const item = instance.items[index];
    if (!item) {
      return 0;
    }

    const itemRect = item.getBoundingClientRect();
    const listRect = instance.list.getBoundingClientRect();
    return itemRect.left - listRect.left + instance.list.scrollLeft;
  }

  getNearestIndex(instance) {
    const currentLeft = instance.list.scrollLeft;
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    instance.items.forEach((item, index) => {
      const distance = Math.abs(currentLeft - this.getItemLeft(instance, index));
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  clampIndex(instance, index) {
    return Math.max(0, Math.min(index, instance.items.length - 1));
  }

  scrollToIndex(instance, index, smooth = true) {
    const targetIndex = this.clampIndex(instance, index);
    instance.currentIndex = targetIndex;
    instance.targetIndex = targetIndex;
    instance.isProgrammaticScroll = smooth;
    instance.scrollEndChecks = 0;

    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
      instance.scrollEndTimer = null;
    }

    instance.list.scrollTo({
      left: this.getItemLeft(instance, targetIndex),
      behavior: smooth ? 'smooth' : 'auto',
    });
    this.sync(instance);

    if (smooth) {
      this.scheduleProgrammaticScrollEnd(instance);
    }
  }

  handleThumbClick(instance, event) {
    const button = event.target.closest('.l-gallery-large__thumb');
    if (!button) {
      return;
    }

    this.scrollToIndex(instance, Number(button.dataset.index));
  }

  handleScroll(instance) {
    if (instance.scrollRaf) {
      cancelAnimationFrame(instance.scrollRaf);
    }

    instance.scrollRaf = requestAnimationFrame(() => {
      instance.scrollRaf = null;

      if (instance.isProgrammaticScroll) {
        this.scheduleProgrammaticScrollEnd(instance);

        if (this.isAtTarget(instance)) {
          this.finishProgrammaticScroll(instance);
        }
        return;
      }

      instance.currentIndex = this.clampIndex(instance, this.getNearestIndex(instance));
      this.sync(instance);
    });
  }

  scheduleProgrammaticScrollEnd(instance) {
    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
    }

    instance.scrollEndTimer = setTimeout(() => {
      instance.scrollEndTimer = null;

      if (!instance.isProgrammaticScroll) {
        return;
      }

      if (this.isAtTarget(instance)) {
        this.finishProgrammaticScroll(instance);
        return;
      }

      instance.scrollEndChecks += 1;
      if (instance.scrollEndChecks >= 10) {
        instance.list.scrollTo({
          left: this.getItemLeft(instance, instance.targetIndex),
          behavior: 'auto',
        });
        this.finishProgrammaticScroll(instance);
        return;
      }

      this.scheduleProgrammaticScrollEnd(instance);
    }, 160);
  }

  isAtTarget(instance) {
    const targetLeft = this.getItemLeft(instance, instance.targetIndex);
    return Math.abs(instance.list.scrollLeft - targetLeft) <= 2;
  }

  finishProgrammaticScroll(instance) {
    if (!instance.isProgrammaticScroll) {
      return;
    }

    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
      instance.scrollEndTimer = null;
    }

    instance.isProgrammaticScroll = false;
    instance.scrollEndChecks = 0;
    instance.currentIndex = instance.targetIndex;
    this.sync(instance, false);
  }

  cancelProgrammaticScroll(instance) {
    if (!instance.isProgrammaticScroll) {
      return;
    }

    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
      instance.scrollEndTimer = null;
    }

    instance.isProgrammaticScroll = false;
    instance.scrollEndChecks = 0;
    instance.list.scrollTo({
      left: instance.list.scrollLeft,
      behavior: 'auto',
    });
    instance.currentIndex = this.clampIndex(instance, this.getNearestIndex(instance));
    instance.targetIndex = instance.currentIndex;
    this.sync(instance, false);
  }

  handleResize(instance) {
    if (instance.resizeRaf) {
      cancelAnimationFrame(instance.resizeRaf);
    }

    instance.resizeRaf = requestAnimationFrame(() => {
      instance.resizeRaf = null;
      instance.list.scrollTo({
        left: this.getItemLeft(instance, instance.currentIndex),
        behavior: 'auto',
      });
      this.sync(instance, false);
    });
  }

  sync(instance, smooth = true) {
    if (!instance.nav) {
      return;
    }

    instance.nav.thumbButtons.forEach((button, index) => {
      const isActive = index === instance.currentIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    instance.nav.prevButton.disabled = instance.currentIndex <= 0;
    instance.nav.nextButton.disabled = instance.currentIndex >= instance.items.length - 1;

    const activeThumb = instance.nav.thumbButtons[instance.currentIndex];
    if (activeThumb) {
      const thumbItem = activeThumb.closest('.l-gallery-large__thumb-item');
      if (!thumbItem) {
        return;
      }

      const thumbsRect = instance.nav.thumbs.getBoundingClientRect();
      const thumbItemRect = thumbItem.getBoundingClientRect();
      const scrollLeft = thumbItemRect.left - thumbsRect.left + instance.nav.thumbs.scrollLeft;

      instance.nav.thumbs.scrollTo({
        left: scrollLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }
}
